import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';
import { useGetAffiliatesData, useUpdateCommission, useGetFilterOptions, useGetAllAffiliatesData } from '../../services/affiliate';
import { useAppContext } from '../context';
import useDebounce from '../useDebounce';

interface AffiliateFilters {
  planName: string;
  planInterval: string;
  startDate: Date | null;
  endDate: Date | null;
}

interface EditingCommission {
  id: string;
  amount: number | string;
}

export const useAffiliateManagement = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchedText, setSearchedText] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [editingCommission, setEditingCommission] = useState<EditingCommission | null>(null);
  const [filters, setFilters] = useState<AffiliateFilters>({
    planName: '',
    planInterval: '',
    startDate: null,
    endDate: null,
  });

  const debouncedSearch = useDebounce(searchedText, 500);

  const columns = [
    { id: 'userName', label: 'User Name' },
    { id: 'userEmail', label: 'User Email' },
    { id: 'affiliateId', label: 'Affiliate ID' },
    { id: 'receiptId', label: 'Receipt ID' },
    { id: 'planName', label: 'Plan Name' },
    { id: 'planInterval', label: 'Plan Interval' },
    { id: 'planAmount', label: 'Plan Amount' },
    { id: 'commissionAmountPaid', label: 'Commission Amount Paid' },
    { id: 'eventType', label: 'Event Type' },
    { id: 'clickCommissionPaid', label: 'Click Commission Paid' },
    { id: 'saleCommissionPaid', label: 'Sale Commission Paid' },
    { id: 'accountDetails', label: 'Account Details' },
  ];

  const { push } = useRouter();
  const { userData, isUserDataLoading } = useAppContext();
  const { enqueueSnackbar } = useSnackbar();

  const { mutateAsync: getAllAffiliatesData } = useGetAllAffiliatesData();
  const { mutateAsync: getAffiliatesData, isLoading } = useGetAffiliatesData();
  const { mutateAsync: updateCommissionMutation } = useUpdateCommission();
  const { data: filterOptions } = useGetFilterOptions();

  const handleRedirectToDashboard = () => {
    push('/dashboard');
  };

  const fetchAffiliates = async (searchText: string, currentFilters: AffiliateFilters, currentPage: number = 0, currentRowsPerPage: number) => {
    try {
      const params = {
        affiliateId: searchText,
        planName: currentFilters.planName || undefined,
        planInterval: currentFilters.planInterval || undefined,
        startDate: currentFilters.startDate ? currentFilters.startDate.toISOString().split('T')[0] : undefined,
        endDate: currentFilters.endDate ? currentFilters.endDate.toISOString().split('T')[0] : undefined,
        page: currentPage,
        limit: currentRowsPerPage,
      };

      const res = await getAffiliatesData(params);
      setData(res.data);
      setTotal(res.total);
    } catch (error) {
      console.error('Error fetching affiliates:', error);
      enqueueSnackbar('Failed to fetch affiliate data', { variant: 'error' });
    }
  };

  const handleSearchInputChange = (val: string) => {
    setSearchedText(val);
    setPage(0);
  };

  const handleFilterChange = async (filterKey: keyof AffiliateFilters, value: any) => {
    const newFilters = { ...filters, [filterKey]: value };
    setFilters(newFilters);
    setPage(0);
    await fetchAffiliates(debouncedSearch, newFilters, 0, rowsPerPage);
  };

  const handleDateRangeChange = async (dateKey: 'startDate' | 'endDate', date: Date | null) => {
    const newFilters = { ...filters, [dateKey]: date };

    // Validate date range
    if (dateKey === 'startDate' && date && newFilters.endDate && date > newFilters.endDate) {
      enqueueSnackbar('Start date cannot be greater than end date', { variant: 'error' });
      return;
    }

    if (dateKey === 'endDate' && date && newFilters.startDate && date < newFilters.startDate) {
      enqueueSnackbar('End date cannot be less than start date', { variant: 'error' });
      return;
    }

    setFilters(newFilters);
    setPage(0);
    await fetchAffiliates(debouncedSearch, newFilters, 0, rowsPerPage);
  };

  const handleChangePage = async (_event: any, newPage: number) => {
    setPage(newPage);
    await fetchAffiliates(debouncedSearch, filters, newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = async (event: any) => {
    const newRowsPerPage = parseInt(event.target.value);
    setPage(0);
    setRowsPerPage(newRowsPerPage);
    await fetchAffiliates(debouncedSearch, filters, 0, newRowsPerPage);
  };

  const handleCommissionEdit = (id: string, amount: number | string) => {
    setEditingCommission({ id, amount });
  };

  const handleCommissionSave = async (id: string) => {
    if (!editingCommission) return;

    // Validate that amount is entered
    const amount = typeof editingCommission.amount === 'string'
      ? parseFloat(editingCommission.amount)
      : editingCommission.amount;

    if (isNaN(amount) || editingCommission.amount === '' || editingCommission.amount === null || editingCommission.amount === undefined) {
      enqueueSnackbar('Please enter an amount', { variant: 'error' });
      return;
    }

    try {
      const updatedData = await updateCommissionMutation({
        id,
        data: {
          commissionAmountPaid: amount,
        },
      });

      setData((prevData) =>
        prevData.map((item) => (item.id === id ? { ...item, ...updatedData } : item))
      );

      setEditingCommission(null);
      enqueueSnackbar('Commission updated successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error updating commission:', error);
      enqueueSnackbar('Failed to update commission', { variant: 'error' });
    }
  };

  const handleCommissionCancel = () => {
    setEditingCommission(null);
  };

  const handleCommissionPaidToggle = async (id: string, field: string, checked: boolean) => {
    try {
      const updatedData = await updateCommissionMutation({
        id,
        data: {
          [field]: checked,
        },
      });

      setData((prevData) =>
        prevData.map((item) => (item.id === id ? { ...item, ...updatedData } : item))
      );

      // Convert field name to user-friendly text
      const fieldDisplayName = field === 'clickCommissionPaid'
        ? 'Click Commission Paid'
        : field === 'saleCommissionPaid'
        ? 'Sale Commission Paid'
        : field;

      enqueueSnackbar(`${fieldDisplayName} updated successfully`, { variant: 'success' });
    } catch (error) {
      console.error('Error updating commission paid status:', error);
      enqueueSnackbar('Failed to update commission paid status', { variant: 'error' });
    }
  };

  // Format date function - for table display (date only)
  const formatDateOnly = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }).replace(/\//g, '-');
  };

  // Format date function - for CSV export (date + time)
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }).replace(/\//g, '-') + ' ' + date.toLocaleTimeString();
  };

  const handleDownloadCSV = () => {
    try {
      // CSV Headers
      const headers = [
        'User Name',
        'User Email',
        'Affiliate ID',
        'Receipt ID',
        'Plan Name',
        'Plan Interval',
        'Plan Amount',
        'Commission Amount Paid',
        'Event Type',
        'Event Date',
        'Click Commission Paid',
        'Sale Commission Paid',
        'Account Details',
        'Created At',
        'Updated At'
      ];

      // Helper function to escape CSV values
      const escapeCSVValue = (value: any): string => {
        if (value === null || value === undefined) {
          return '';
        }
        const stringValue = String(value);
        // If the value contains comma, quote, or newline, wrap it in quotes and escape internal quotes
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      };

      // Convert current page data to CSV format
      const csvRows = [
        headers.join(','), // Header row
        ...data.map(row => [
          escapeCSVValue(row.userName || ''),
          escapeCSVValue(row.userEmail || ''),
          escapeCSVValue(row.affiliateId || ''),
          escapeCSVValue(row.receiptId || ''),
          escapeCSVValue(row.planName || ''),
          escapeCSVValue(row.planInterval || ''),
          escapeCSVValue(row.planAmount ? `$${row.planAmount}` : ''),
          escapeCSVValue(row.commissionAmountPaid ? `$${row.commissionAmountPaid}` : '$0'),
          escapeCSVValue(row.eventType || ''),
          escapeCSVValue(formatDate(row.eventDate)), // CSV export includes time
          escapeCSVValue(row.clickCommissionPaid ? 'Paid' : 'Unpaid'),
          escapeCSVValue(row.saleCommissionPaid ? 'Paid' : 'Unpaid'),
          escapeCSVValue(formatDate(row.createdAt)),
          escapeCSVValue(formatDate(row.updatedAt))
        ].join(','))
      ];

      const csvContent = csvRows.join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `affiliate-sales-report-${formatDate(new Date().toISOString())}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      enqueueSnackbar('CSV downloaded successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error downloading CSV:', error);
      enqueueSnackbar('Failed to download CSV', { variant: 'error' });
    }
  };

  const handleDownloadAll = async () => {
    setIsDownloading(true);
    try {
      // Get timezone offset in minutes (negative for timezones ahead of UTC)
      const timezoneOffset = new Date().getTimezoneOffset();
      const url = await getAllAffiliatesData(timezoneOffset);

      const response = await fetch(url);
      const blob = await response.blob();

      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('download', `all-affiliates-data-sales-report-${formatDate(new Date().toISOString())}.csv`);
      link.href = blobUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.log(error);
      enqueueSnackbar('Failed to download CSV', { variant: 'error' });
    }
    setIsDownloading(false);
  };

  // Debounced search effect
  useEffect(() => {
    if (userData?.isAdmin) {
      fetchAffiliates(debouncedSearch, filters, 0, rowsPerPage);
    }
  }, [debouncedSearch, filters, userData?.isAdmin]);

  // Initial data fetch
  useEffect(() => {
    if (userData?.isAdmin) {
      fetchAffiliates('', filters, 0, rowsPerPage);
    }
  }, [userData?.isAdmin]);

  return {
    columns,
    userData,
    isUserDataLoading,
    isLoading,
    handleRedirectToDashboard,
    handleDownloadCSV,
    handleDownloadAll,
    isDownloading,
    page,
    rowsPerPage,
    data,
    total,
    handleChangePage,
    handleChangeRowsPerPage,
    searchedText,
    handleSearchInputChange,
    filters,
    handleFilterChange,
    handleDateRangeChange,
    editingCommission,
    handleCommissionEdit,
    handleCommissionSave,
    handleCommissionCancel,
    handleCommissionPaidToggle,
    filterOptions,
    enqueueSnackbar,
    setFilters,
    setPage,
    fetchAffiliates,
    debouncedSearch,
    formatDateOnly, // For table display (date only)
  };
};
